import { Routes } from '@angular/router';
import { RoleAuthGuard } from './core/guards/role-auth.guard';
import { ADMIN_ROLES, ORG_ADMIN_ONLY, ANY_EKMAAS_ROLE } from './core/models/role-mapping.const';
import { azureByokCloudProviderData } from './features/cloud-clients/shared/cloud-providers/azure-byok-provider';
import { microsoftDkeCloudProviderData } from './features/cloud-clients/shared/cloud-providers/microsoft-dke-provider';
import { azureHyokCloudProviderData } from '@kmaas-features/cloud-clients/shared/cloud-providers/azure-hyok-provider';
import { googleHyokCloudProviderData } from '@kmaas-features/cloud-clients/shared/cloud-providers/google-hyok-provider';

export const routes: Routes = [
  { path: '', redirectTo: 'home', pathMatch: 'full' },
  {
    path: 'home',
    loadComponent: () => import('./features/homepage/taas-home.component').then((module) => module.TaasHomeComponent),
  },
  {
    path: 'cloud-clients',
    children: [
      { path: '', redirectTo: 'azure-byok', pathMatch: 'full' },
      {
        path: 'azure-byok',
        loadComponent: () =>
          import('./features/cloud-clients/instances/instances.component').then((module) => module.InstancesComponent),
        canActivate: [RoleAuthGuard],
        data: {
          requiredRoles: ANY_EKMAAS_ROLE,
          provider: azureByokCloudProviderData,
        },
      },
      {
        path: 'microsoft-dke',
        loadComponent: () =>
          import('./features/cloud-clients/instances/instances.component').then((module) => module.InstancesComponent),
        canActivate: [RoleAuthGuard],
        data: {
          requiredRoles: ANY_EKMAAS_ROLE,
          provider: microsoftDkeCloudProviderData,
        },
      },
      {
        path: 'azure-hyok',
        loadComponent: () =>
          import('./features/cloud-clients/instances/instances.component').then((module) => module.InstancesComponent),
        canActivate: [RoleAuthGuard],
        data: {
          requiredRoles: ANY_EKMAAS_ROLE,
          provider: azureHyokCloudProviderData,
        },
      },
      {
        path: 'google-hyok',
        loadComponent: () =>
          import('./features/cloud-clients/instances/instances.component').then((module) => module.InstancesComponent),
        canActivate: [RoleAuthGuard],
        data: {
          requiredRoles: ANY_EKMAAS_ROLE,
          provider: googleHyokCloudProviderData,
        },
      },
      {
        path: ':cloudType/:id/keys',
        loadComponent: () => import('./features/cloud-clients/keys/keys.component').then((m) => m.KeysComponent),
      },
    ],
    canActivateChild: [RoleAuthGuard],
    data: {
      requiredRoles: ANY_EKMAAS_ROLE,
    },
  },
  {
    path: 'managekeys',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/manage-cloud-keys/taas-manage-cloud-keys.component').then(
            (module) => module.TaasManageCloudKeysComponent,
          ),
        canActivate: [RoleAuthGuard],
      },
      {
        path: ':id',
        loadComponent: () =>
          import('./features/manage-cloud-keys/taas-manage-cloud-keys.component').then(
            (module) => module.TaasManageCloudKeysComponent,
          ),
        canActivate: [RoleAuthGuard],
      },
      {
        path: ':cloudType/:id',
        loadComponent: () =>
          import('./features/manage-cloud-keys/taas-manage-cloud-keys.component').then(
            (module) => module.TaasManageCloudKeysComponent,
          ),
        canActivate: [RoleAuthGuard],
      },
    ],
    canActivateChild: [RoleAuthGuard],
    data: {
      requiredRoles: ANY_EKMAAS_ROLE,
    },
  },
  {
    path: 'audit-logs',
    loadComponent: () =>
      import('./features/audit-log/taas-audit-log.component').then((module) => module.TaasAuditLogComponent),
    canActivate: [RoleAuthGuard],
    data: {
      requiredRoles: ANY_EKMAAS_ROLE,
    },
  },
  {
    path: 'access-management',
    children: [
      {
        path: 'users',
        loadComponent: () =>
          import('./features/access-management/view-user/taas-view-user.component').then(
            (module) => module.TaasViewUserComponent,
          ),
        canActivate: [RoleAuthGuard],
        data: {
          requiredRoles: ADMIN_ROLES,
        },
      },
    ],
    canActivateChild: [RoleAuthGuard],
    data: {
      requiredRoles: ADMIN_ROLES,
    },
  },
  {
    path: 'hsm',
    children: [
      { path: '', redirectTo: 'connect-hsm', pathMatch: 'full' },
      {
        path: 'connect-hsm',
        loadComponent: () =>
          import('./features/hsm/connect-hsm/taas-connect-hsm.component').then(
            (module) => module.TaasConnectHsmComponent,
          ),
        canActivate: [RoleAuthGuard],
        data: {
          requiredRoles: ORG_ADMIN_ONLY,
          ifCanManageHsm: true,
        },
      },
      {
        path: 'modify-hsm',
        loadComponent: () =>
          import('./features/hsm/modify-hsm/taas-modify-hsm.component').then((module) => module.TaasModifyHsmComponent),
        canActivate: [RoleAuthGuard],
        data: {
          requiredRoles: ORG_ADMIN_ONLY,
          ifCanManageHsm: true,
        },
      },
    ],
    canActivateChild: [RoleAuthGuard],
    data: {
      requiredRoles: ORG_ADMIN_ONLY,
      ifCanManageHsm: true,
    },
  },
  {
    path: 'hsm-list',
    loadComponent: () =>
      import('./features/hsm/list-hsm/taas-list-hsm.component').then((module) => module.TaasListHsmComponent),
    canActivate: [RoleAuthGuard],
    data: {
      requiredRoles: ORG_ADMIN_ONLY,
    },
  },
  {
    path: 'enterprise-clients',
    children: [
      {
        path: 'kmip',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/enterprise-clients/kmip/taas-kmip.component').then(
                (module) => module.TaasKmipComponent,
              ),
            canActivate: [RoleAuthGuard],
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/enterprise-clients/kmip/taas-manage-kmip-client/taas-manage-kmip-client.component').then(
                (module) => module.TaasManageKmipClientComponent,
              ),
            canActivate: [RoleAuthGuard],
          },
        ],
        canActivateChild: [RoleAuthGuard],
        data: {
          requiredRoles: ANY_EKMAAS_ROLE,
        },
      },
      {
        path: 'tde',
        loadComponent: () =>
          import('./features/enterprise-clients/tde/taas-tde.component').then((module) => module.TaasTdeComponent),
        canActivate: [RoleAuthGuard],
        data: {
          requiredRoles: ANY_EKMAAS_ROLE,
        },
      },
      {
        path: 'rest',
        loadComponent: () =>
          import('./features/enterprise-clients/rest/taas-rest.component').then((module) => module.TaasRestComponent),
        canActivate: [RoleAuthGuard],
        data: {
          requiredRoles: ANY_EKMAAS_ROLE,
        },
      },
    ],
    canActivateChild: [RoleAuthGuard],
    data: {
      requiredRoles: ANY_EKMAAS_ROLE,
    },
  },
  {
    path: 'no-access',
    loadComponent: () =>
      import('./features/error/taas-no-access.component').then((module) => module.TaasNoAccessComponent),
  },
];
